import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import { acceptDuelInvite, declineDuelInvite, revokeDuelInvite } from '../../api/duels';
import '../../css/duels/InviteList.css';

const STATUS_LABELS = {
    PENDING: 'Ожидает ответа',
    ACCEPTED: 'Принято',
    DECLINED: 'Отклонено',
    REVOKED: 'Отозвано',
    EXPIRED: 'Истёк срок'
};

const STATUS_COLORS = {
    PENDING: 'warning',
    ACCEPTED: 'success',
    DECLINED: 'danger',
    REVOKED: 'secondary',
    EXPIRED: 'secondary'
};

const INVITE_TYPE_LABELS = {
    PARTICIPANT: 'Участник',
    JUDGE: 'Судья'
};

/**
 * Список приглашений для дуэли
 * @param {Array} invites - Список приглашений
 * @param {boolean} loading - Состояние загрузки
 * @param {string} currentUserId - ID текущего пользователя
 * @param {boolean} isCreator - Является ли текущий пользователь создателем дуэли
 * @param {Function} onInviteAction - Callback после действия с приглашением
 */
export default function InviteList({ 
    invites = [], 
    loading = false, 
    currentUserId, 
    isCreator,
    onInviteAction 
}) {
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);

    const handleAccept = async (inviteId) => {
        setActionLoading(inviteId);
        setError(null);
        try {
            await acceptDuelInvite(inviteId);
            if (onInviteAction) onInviteAction();
        } catch (err) {
            console.error('Ошибка при принятии приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось принять приглашение');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDecline = async (inviteId) => {
        setActionLoading(inviteId);
        setError(null);
        try {
            await declineDuelInvite(inviteId);
            if (onInviteAction) onInviteAction();
        } catch (err) {
            console.error('Ошибка при отклонении приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось отклонить приглашение');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRevoke = async (inviteId) => {
        setActionLoading(inviteId);
        setError(null);
        try {
            await revokeDuelInvite(inviteId);
            if (onInviteAction) onInviteAction();
        } catch (err) {
            console.error('Ошибка при отзыве приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось отозвать приглашение');
        } finally {
            setActionLoading(null);
        }
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <HrnbLoader />;
    }

    if (invites.length === 0) {
        return (
            <div className="invite-list invite-list--empty">
                <p className="invite-list__empty">Приглашений пока нет</p>
            </div>
        );
    }

    return (
        <div className="invite-list">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="invite-list__items">
                {invites.map((invite) => {
                    const isInvitee = invite.invitee?.id === currentUserId;
                    const isInviter = invite.inviter?.id === currentUserId;
                    const canAccept = isInvitee && invite.status === 'PENDING';
                    const canRevoke = isInviter && invite.status === 'PENDING';

                    return (
                        <HrnbCard key={invite.id} className="invite-list__item">
                            <div className="invite-list__header">
                                <span className={`invite-list__status invite-list__status--${STATUS_COLORS[invite.status]}`}>
                                    {STATUS_LABELS[invite.status] || invite.status}
                                </span>
                                <span className="invite-list__type">
                                    {INVITE_TYPE_LABELS[invite.inviteType]}
                                </span>
                            </div>

                            <div className="invite-list__body">
                                <div className="invite-list__user">
                                    <span className="invite-list__label">
                                        {isInvitee ? 'От:' : 'Кому:'}
                                    </span>
                                    <Link
                                        to={`/profile/${invite.inviter?.profileId || invite.inviter?.id}`}
                                        className="invite-list__username"
                                    >
                                        {invite.inviter?.nickname || invite.inviter?.name || 'Аноним'}
                                    </Link>
                                </div>

                                {invite.message && (
                                    <p className="invite-list__message">{invite.message}</p>
                                )}

                                <div className="invite-list__meta">
                                    <span className="invite-list__date">
                                        Создано: {formatDate(invite.createdAt)}
                                    </span>
                                    {invite.expiresAt && invite.status === 'PENDING' && (
                                        <span className="invite-list__expires">
                                            Действует до: {formatDate(invite.expiresAt)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="invite-list__actions">
                                {canAccept && (
                                    <>
                                        <HrnbButton
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleAccept(invite.id)}
                                            disabled={actionLoading === invite.id}
                                        >
                                            {actionLoading === invite.id ? 'Принятие...' : 'Принять'}
                                        </HrnbButton>
                                        <HrnbButton
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDecline(invite.id)}
                                            disabled={actionLoading === invite.id}
                                        >
                                            {actionLoading === invite.id ? 'Отклонение...' : 'Отклонить'}
                                        </HrnbButton>
                                    </>
                                )}

                                {canRevoke && (
                                    <HrnbButton
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleRevoke(invite.id)}
                                        disabled={actionLoading === invite.id}
                                    >
                                        {actionLoading === invite.id ? 'Отзыв...' : 'Отозвать'}
                                    </HrnbButton>
                                )}
                            </div>
                        </HrnbCard>
                    );
                })}
            </div>
        </div>
    );
}
